import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import {DataService} from '../../../../Services/data.service';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { PaymentTypeModel } from '../../../../DataModels/payment-type-model';
import { ConfirmDialogService } from '../../../../Services/confirm-dialog.service';
import { BsToastService } from '../../../../Services/bs-toast-service';
import {Title} from '@angular/platform-browser';

@Component({
  selector: 'app-payment-type-form',
  templateUrl: './payment-type-form.component.html',
  styleUrls: ['./payment-type-form.component.css'],
  standalone: false
})
export class PaymentTypeFormComponent implements OnInit {
  paymentTypeID: string;
  paymentType: PaymentTypeModel;
  paymentTypeForm: UntypedFormGroup;

  paid = false;
  showDelete = false;
  submitted = false;
  originalSnapshot: any = {};

  buttonConfig: any = {
    save: true,
    close: true,
    reset: true,
    delete: true,
    saveNew: true,
    saveClose: true,
    print: false,
  };

  constructor(
    private table: DataService,
    private notification: BsToastService,
    private formBuilder: UntypedFormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private confirmDialogService: ConfirmDialogService,
    private titleService: Title
  ) {
    this.createForm();
    this.paymentTypeID = this.route.snapshot.params.id;
  }

  get f() {
    return this.paymentTypeForm.controls;
  }

  createForm() {
    this.paymentTypeForm = this.formBuilder.group({
      name: new UntypedFormControl(''),
      active: new UntypedFormControl(true),
      print_check: new UntypedFormControl(false),
      provider: new UntypedFormControl('Manual'),
      stripe_enabled: new UntypedFormControl(false),
      stripe_flow: new UntypedFormControl('ACH'),
      stripe_requires_verified_method: new UntypedFormControl(true),
      stripe_allow_saved_method: new UntypedFormControl(true),
      stripe_default_currency: new UntypedFormControl('USD'),
    });
  }

  ngOnInit() {
    this.titleService.setTitle($localize`:Payment Type@@paymentType:Payment Type`);
    if (this.paymentTypeID) {
      this.LoadData();
      this.buttonConfig.reset = false;
    } else {
      Promise.resolve().then(() => {
        try {
          this.originalSnapshot = this.buildSnapshot();
        } catch {
        }
      });
    }
  }

  private LoadData() {
    this.table.getData('payment_type', this.paymentTypeID).subscribe(
      (result: any) => {
        const rawData = (result && result.Data !== undefined) ? result.Data : result;
        const data = Array.isArray(rawData) ? (rawData[0] || {}) : (rawData || {});
        this.paymentType = data as PaymentTypeModel;
        this.paymentTypeForm.patchValue(this.paymentType);
        try {
          this.originalSnapshot = this.buildSnapshot();
        } catch {
        }
      },
      (error: HttpErrorResponse) => {
        console.log(
          error +
            ' - ' +
          $localize`:Error loading Payment Type @@errorLoadingPaymentType:Error loading Payment Type`
        );
      }
    );
  }

  saveClose() {
    if (this.onSave(true, false)) {
    }
  }

  saveNew() {
    // Defer reset until async save completes inside onSave(false, true)
    this.onSave(false, true);
  }

  resetForm() {
    this.resetToDefaults();
    try {
      this.originalSnapshot = this.buildSnapshot();
    } catch {
    }
  }

  onClose() {
    this.router.navigate(['admin/payment-type-list']).then();
  }

  private resetToDefaults(): void {
    // Reset the form to known default values and clear component state for a fresh entry
    this.paymentTypeForm.reset({
      name: '',
      active: true,
      print_check: false,
      provider: 'Manual',
      stripe_enabled: false,
      stripe_flow: 'ACH',
      stripe_requires_verified_method: true,
      stripe_allow_saved_method: true,
      stripe_default_currency: 'USD',
    });

    this.paymentType = {
      name: '',
      active: true,
      print_check: false,
      provider: 'Manual',
      stripe_enabled: false,
      stripe_flow: 'ACH',
      stripe_requires_verified_method: true,
      stripe_allow_saved_method: true,
      stripe_default_currency: 'USD',
    } as PaymentTypeModel;
    this.paymentTypeID = null;
    try {
      this.originalSnapshot = this.buildSnapshot();
    } catch {
    }
  }

  deleteItem() {
    let retMsg = '';
    const messages: string[] = [
      $localize`:Are you sure you want to delete payment type? @@areYouSureYouWantToDeletePaymentType:Are you sure you want to delete payment type?`,
      this.paymentType.name,
    ];
    this.confirmDialogService.confirmThis(
      messages,
      () => {
        this.table.deleteDataById('payment_type', this.paymentTypeID).subscribe(
          (result) => {
            retMsg = result;
            // this.notification.success($localize`:Deleted @@deleted:Deleted`);
          },
          (error: HttpErrorResponse) => {
            // this.notification.error($localize`:Error @@error:Error`);
            console.log(error);
          },
          () => {
            this.onClose();
          }
        );
      },
      () => {
        this.notification.showSuccessToast($localize`:Delete Canceled@@deleteCanceled:Delete Canceled`);
      }
    );
  }

  onSave(saveClose: boolean, saveNew = false) {
    this.submitted = true;
    if (this.paymentTypeForm.invalid) {
      this.notification.showWarningToast($localize`:Form Errors! @@formErrors:Form Errors!`);
      return;
    }

    const provider = this.paymentTypeForm.get('provider')?.value;
    if (provider !== 'Stripe') {
      this.paymentTypeForm.patchValue(
        {
          stripe_enabled: false,
          stripe_flow: null,
          stripe_requires_verified_method: false,
          stripe_allow_saved_method: false,
          stripe_default_currency: null,
        },
        {emitEvent: false}
      );
    }

    this.paymentType = Object.assign({}, this.paymentTypeForm.value);
    if (this.paymentTypeID) {
      if (this.paymentTypeForm.invalid === false) {
        this.table.patch('payment_type', this.paymentTypeID, this.paymentType).then(
          (result) => {
            try {
              this.originalSnapshot = this.buildSnapshot();
            } catch {
            }
            if (saveClose) {
              this.onClose();
            } else if (saveNew) {
              this.resetToDefaults();
            } else {
              this.LoadData();
            }
          },
          (error: HttpErrorResponse) => {
            //
            console.log(error);
          }
        );
        // return true;
      }
    } else {
      this.table.postWithPromise('payment_type', this.paymentType).then((res) => {
        // stops double posting when clicking Save
        let apiReturn: any = null;
        if (typeof res === 'object') {
          apiReturn = JSON.parse(JSON.stringify(res));
        }
        this.paymentTypeID = apiReturn.id;
        try {
          this.originalSnapshot = this.buildSnapshot();
        } catch {
        }
        if (saveClose) {
          this.onClose();
        } else if (saveNew) {
          this.resetToDefaults();
        } else {
          this.LoadData();
        }
      });
      // return true;
    }
    this.submitted = false;
    return true;
  }

  buildSnapshot(): any {
    try {
      return this.paymentTypeForm.getRawValue();
    } catch {
      return {};
    }
  }

  hasChanges(): boolean {
    try {
      const current = this.buildSnapshot();
      const original = this.originalSnapshot || {};
      return JSON.stringify(current) !== JSON.stringify(original);
    } catch {
      return false;
    }
  }

  checkForChanges(): boolean {
    return this.hasChanges();
  }
}
